// ── Resource DTOs ──────────────────────────────────────────────────────────────
//
// API contracts — what clients send (ResourceCreate/ResourceUpdate) and receive (Resource).
// No Mongoose, no DB details. Those live in resource.model.ts.

import { z } from "zod";

// ── ResourceStatus ─────────────────────────────────────────────────────────────

export const ResourceStatus = {
  DRAFT: "draft",
  INDEXING: "indexing",
  INDEXING_ERROR: "indexing_error",
  PUBLISHED: "published",
  ARCHIVED: "archived",
} as const;

export type ResourceStatus = (typeof ResourceStatus)[keyof typeof ResourceStatus];

// ── EmbeddingProvider ──────────────────────────────────────────────────────────

export const EmbeddingProvider = {
  OPENAI: "openai",
  JINA: "jina",
} as const;

export type EmbeddingProvider = (typeof EmbeddingProvider)[keyof typeof EmbeddingProvider];

// ── DocumentParser ─────────────────────────────────────────────────────────────

export const DocumentParser = {
  SEMANTIC_MARKDOWN: "semantic-markdown",
  FAQ_ENRICHED: "faq-enriched",
  ATA_CODES: "ata-codes",
} as const;

export type DocumentParser = (typeof DocumentParser)[keyof typeof DocumentParser];

// ── Resource ───────────────────────────────────────────────────────────────────

export const Resource = z.object({
  id: z.string().describe("Unique resource identifier"),
  title: z.string().min(1).describe("Resource title"),
  description: z.string().describe("Resource description"),
  version: z.number().int().min(0).describe("Resource version number"),
  status: z
    .enum(Object.values(ResourceStatus) as [string, ...string[]])
    .describe("Resource lifecycle status"),
  team_id: z.string().min(1).describe("Owning team ID"),
  created_by: z.string().min(1).describe("Creator user ID"),
  last_edited_by: z.string().optional().describe("Last editor user ID"),
  published_at: z.coerce.date().optional().describe("Timestamp when resource was published"),
  draft_content: z.string().describe("Draft content text"),
  published_content: z.string().describe("Published content text"),
  vector_db_index_name: z.string().optional().describe("Vector database index name"),
  language: z.string().describe("Content language code"),
  embedding_provider: z
    .enum(Object.values(EmbeddingProvider) as [string, ...string[]])
    .optional()
    .describe("Embedding provider for vector indexing"),
  document_parser: z
    .enum(Object.values(DocumentParser) as [string, ...string[]])
    .optional()
    .describe("Document parser strategy"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
});

export type Resource = z.infer<typeof Resource>;

// ── ResourceCreate ─────────────────────────────────────────────────────────────

export const ResourceCreate = Resource.omit({
  id: true,
  published_at: true, // set by service when status transitions to "published"
  created_at: true,
  updated_at: true,
}).extend({
  status: z
    .enum(Object.values(ResourceStatus) as [string, ...string[]])
    .default(ResourceStatus.DRAFT),
  version: z.number().int().min(0).default(0),
  language: z.string().default("en"),
  draft_content: z.string().default(""),
  published_content: z.string().default(""),
});

export type ResourceCreate = z.infer<typeof ResourceCreate>;

// ── ResourceUpdate ─────────────────────────────────────────────────────────────
// team_id and created_by are immutable after creation.
// published_at is managed by the service — never user-supplied.
// Fields that carry .default() in ResourceCreate are re-declared without defaults
// so Zod does not inject values when they are absent from a PATCH body —
// preserving true partial-update semantics.

export const ResourceUpdate = ResourceCreate.omit({
  team_id: true,
  created_by: true,
})
  .partial()
  .extend({
    status: z.enum(Object.values(ResourceStatus) as [string, ...string[]]).optional(),
    version: z.number().int().min(0).optional(),
    language: z.string().optional(),
    draft_content: z.string().optional(),
    published_content: z.string().optional(),
  });

export type ResourceUpdate = z.infer<typeof ResourceUpdate>;
